// Serialize an edited team back into Showdown export format so the existing
// /api/teams POST endpoint (which expects a Showdown string) can round-trip
// builder edits without a new server endpoint.
//
// Stable, deterministic output. Mirrors @pkmn/sets' Team.export shape — only
// emits fields that differ from defaults (e.g. omits EV lines with 0s, omits
// IV lines when all 31). No Tera line in Reg M-A.

// EditorMember is the relaxed member shape the editor operates on. Defined
// here (not imported from Editor.tsx) because the test/build toolchain
// doesn't allow .ts files to import types from .tsx in some configurations.
// Keep this in sync with Editor.tsx's local EditorMember export — both
// re-export from this module would be cleaner but requires a separate
// types module.
type StatBlock = Record<'hp' | 'atk' | 'def' | 'spa' | 'spd' | 'spe', number>;
export type EditorMember = {
  species: string;
  item?: string;
  ability?: string;
  nature?: string;
  level?: number;
  evs?: Partial<StatBlock>;
  ivs?: Partial<StatBlock>;
  moves?: string[];
};

const STAT_LABEL: Record<string, string> = {
  hp: 'HP',
  atk: 'Atk',
  def: 'Def',
  spa: 'SpA',
  spd: 'SpD',
  spe: 'Spe',
};

// Title-case the species id for the export header. "froslass" → "Froslass",
// "charizard-mega-y" → "Charizard-Mega-Y".
function titleCaseSpecies(id: string): string {
  return id
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('-');
}

// Look up item display name. Items in our DB are stored as @pkmn slugs
// ("lifeorb", "choicescarf"); the export format uses human-readable
// ("Life Orb", "Choice Scarf"). We don't have a slug→display map server-side
// yet, so heuristically: split on transitions from lower→upper, then title-
// case. Falls back to capitalizing each word if the slug already has spaces.
function displayName(slug: string): string {
  if (!slug) return '';
  if (slug.includes(' ')) {
    return slug
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  // Two-pass: insert spaces at word boundaries (no boundaries detectable in
  // pure-lowercase slugs like "lifeorb"), then title-case each word.
  // Honest gap: "lifeorb" → "Lifeorb", not "Life Orb". The agent's prompt
  // accepts both forms in calc_damage via normalizeItemSlug, and Showdown's
  // import accepts both too. Good enough for round-trip; can refine when
  // we ship a slug→display map.
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

export function serializeMember(m: EditorMember): string {
  const lines: string[] = [];
  // Header: "Species @ Item" — item omitted if absent.
  const species = titleCaseSpecies(m.species);
  const itemPart = m.item ? ` @ ${displayName(m.item)}` : '';
  lines.push(`${species}${itemPart}`);
  if (m.ability) lines.push(`Ability: ${m.ability}`);
  if (m.level && m.level !== 50) lines.push(`Level: ${m.level}`);

  const evParts: string[] = [];
  for (const stat of ['hp', 'atk', 'def', 'spa', 'spd', 'spe'] as const) {
    const v = m.evs?.[stat] ?? 0;
    if (v > 0) evParts.push(`${v} ${STAT_LABEL[stat]}`);
  }
  if (evParts.length > 0) lines.push(`EVs: ${evParts.join(' / ')}`);

  if (m.nature) lines.push(`${m.nature} Nature`);

  // IVs: only emit a line if any IV differs from 31 (the assumed default).
  const ivParts: string[] = [];
  for (const stat of ['hp', 'atk', 'def', 'spa', 'spd', 'spe'] as const) {
    const v = m.ivs?.[stat];
    if (typeof v === 'number' && v !== 31) ivParts.push(`${v} ${STAT_LABEL[stat]}`);
  }
  if (ivParts.length > 0) lines.push(`IVs: ${ivParts.join(' / ')}`);

  for (const mv of m.moves ?? []) {
    if (mv) lines.push(`- ${displayName(mv)}`);
  }

  return lines.join('\n');
}

export function serializeTeam(team: readonly EditorMember[]): string {
  return team
    .filter((m) => m?.species)
    .map(serializeMember)
    .join('\n\n');
}
